/**
 * Media Upload Zone Component
 * Handles ambient photo/video uploads with drag & drop and progress tracking
 */

import { useState, useRef, DragEvent } from 'react';
import { Upload, Loader2, AlertCircle, Film } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface MediaUploadZoneProps {
  onUpload: (files: File[]) => Promise<void>;
  maxMedia?: number;
  maxPhotos?: number;
  currentCount: number;
  uploading?: boolean;
}

export function MediaUploadZone({
  onUpload,
  maxMedia,
  maxPhotos,
  currentCount,
  uploading = false,
}: MediaUploadZoneProps) {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const max = maxMedia ?? maxPhotos ?? 10;
  const canUploadMore = currentCount < max;
  const remaining = max - currentCount;

  const validateFiles = (files: File[]): { valid: File[]; errors: string[] } => {
    const valid: File[] = [];
    const errors: string[] = [];

    if (!canUploadMore) {
      errors.push(`Ya tienes el máximo de ${max} archivos`);
      return { valid, errors };
    }

    const allowedPhotoTypes = ['image/png', 'image/jpg', 'image/jpeg'];
    const allowedVideoTypes = ['video/mp4', 'video/quicktime'];
    const maxPhotoSize = 20 * 1024 * 1024;
    const maxVideoSize = 50 * 1024 * 1024;

    for (const file of files) {
      const isPhoto = allowedPhotoTypes.includes(file.type);
      const isVideo = allowedVideoTypes.includes(file.type);

      if (!isPhoto && !isVideo) {
        errors.push(`${file.name}: Solo se permiten PNG, JPG, JPEG, MP4 o MOV`);
        continue;
      }

      const maxSize = isVideo ? maxVideoSize : maxPhotoSize;
      if (file.size > maxSize) {
        errors.push(`${file.name}: El archivo excede el límite de ${isVideo ? '50MB' : '20MB'}`);
        continue;
      }

      if (valid.length >= remaining) {
        errors.push(`Solo puedes subir ${remaining} archivo(s) más`);
        break;
      }

      valid.push(file);
    }

    return { valid, errors };
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    await handleFiles(files);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFiles = async (files: File[]) => {
    setError(null);

    if (files.length === 0) return;

    const { valid, errors } = validateFiles(files);

    if (errors.length > 0) {
      setError(errors.join('\n'));
    }

    if (valid.length > 0) {
      try {
        await onUpload(valid);
      } catch {
        setError('Error al subir los archivos. Inténtalo de nuevo.');
      }
    }
  };

  const handleDrag = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    await handleFiles(files);
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-4">
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={cn(
          'border-2 border-dashed rounded-lg p-8 text-center transition-colors',
          dragActive && 'border-blue-500 bg-blue-50',
          !dragActive && 'border-gray-300',
          !canUploadMore && 'opacity-50 pointer-events-none'
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpg,image/jpeg,video/mp4,video/quicktime"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          disabled={!canUploadMore || uploading}
        />

        {uploading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="h-12 w-12 text-blue-500 animate-spin mb-4" />
            <p className="text-gray-600">Subiendo archivos...</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-center gap-2 mb-4">
              <Upload className="h-12 w-12 text-gray-400" />
              <Film className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {dragActive
                ? 'Suelta los archivos aquí'
                : 'Arrastra fotos o videos, O haz clic para seleccionar'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Fotos: PNG, JPG, JPEG (máx. 20MB) • Videos: MP4, MOV (máx. 50MB)
            </p>
            <Button
              type="button"
              onClick={triggerFileInput}
              disabled={!canUploadMore || uploading}
              className="min-h-11"
            >
              Seleccionar Archivos
            </Button>
            <p className="text-xs text-gray-500 mt-4">
              {currentCount} de {max} archivos • {remaining} disponible(s)
            </p>
          </>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <p className="text-sm whitespace-pre-line">{error}</p>
        </div>
      )}
    </div>
  );
}

export const PhotoUploadZone = MediaUploadZone;
